import React, { useState, useEffect, useMemo } from "react";
import { MultiSelect } from "primereact/multiselect";
import { getAllNeighborhoods } from "../services/addressService";
import { getAllUsers } from "../services/userService";
import {
  GOREV_ATAMA_RAPORU_EN_FAZLA_GUN as GOREV_ATAMA_EN_FAZLA_GUN,
  getGorevAtamaRaporu,
  getZiyaretciRaporlariOranli,
  raporHataMesaji,
} from "../services/summaryService";
import { ArrowDownIcon } from "../assets/icons";

const RAPOR_TURLERI = [
  {
    value: "ziyaretci",
    label: "Ziyaretçi Raporları (Oranlı)",
    indir: getZiyaretciRaporlariOranli,
    dosyaAdi: "ziyaretci-raporlari-oranli",
  },
  {
    value: "gorevAtama",
    label: "Görev Atama Raporu",
    indir: getGorevAtamaRaporu,
    dosyaAdi: "gorev-atama-raporu",
    aciklama: `Görev Planlama'da personele verilen görevler; en çok ${GOREV_ATAMA_EN_FAZLA_GUN} gün seçilebilir. Excel sayfaları: Özet, Kişi Bazlı, İlçe Bazlı, Mahalle Bazlı, Günlük, Detay.`,
    enFazlaGun: GOREV_ATAMA_EN_FAZLA_GUN,
  },
];

const Raporlama = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [reportType, setReportType] = useState(RAPOR_TURLERI[0].value);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [selectedPersonel, setSelectedPersonel] = useState([]);
  const [personelSearch, setPersonelSearch] = useState("");

  const [neighborhoodOptions, setNeighborhoodOptions] = useState([]);
  const [personelOptions, setPersonelOptions] = useState([]);

  const [isNeighborhoodLoading, setIsNeighborhoodLoading] = useState(false);
  const [isPersonelLoading, setIsPersonelLoading] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    fetchNeighborhoods();
    fetchPersonel();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNeighborhoods = async () => {
    try {
      setIsNeighborhoodLoading(true);
      const response = await getAllNeighborhoods();
      
      // Response structure: { status: 200, message: "Success", data: [{id: "...", ad: "..."}] }
      const neighborhoods = response?.data?.data || response?.data || [];
      
      const normalized = neighborhoods
        .map((item) => ({
          id: String(item.id || ""),
          name: String(item.ad || ""),
        }))
        .filter((item) => item.id && item.name);
      
      setNeighborhoodOptions(normalized);
    } catch (error) {
      console.error("Failed to fetch neighborhoods:", error);
      setNeighborhoodOptions([]);
    } finally {
      setIsNeighborhoodLoading(false);
    }
  };

  const fetchPersonel = async () => {
    try {
      setIsPersonelLoading(true);
      const users = await getAllUsers();
      const normalized = users
        .filter((user) => user.isActive !== false)
        .map((user) => ({
          id: String(user.id ?? user.Id ?? ""),
          name: `${user.name ?? ""} ${user.surname ?? ""}`.trim() || user.email || String(user.id ?? ""),
        }))
        .filter((user) => user.id && user.name)
        .sort((a, b) => a.name.localeCompare(b.name, "tr"));
      setPersonelOptions(normalized);
    } catch (error) {
      console.error("Failed to fetch personel:", error);
      setPersonelOptions([]);
    } finally {
      setIsPersonelLoading(false);
    }
  };

  const handleNeighborhoodChange = (e) => {
    setSelectedNeighborhoods(e.value || []);
  };

  const selectedPersonelIds = useMemo(
    () => new Set(selectedPersonel.map((p) => p.id)),
    [selectedPersonel]
  );

  const filteredPersonel = useMemo(() => {
    const term = personelSearch.trim().toLocaleLowerCase("tr");
    if (!term) return personelOptions;
    return personelOptions.filter((p) => p.name.toLocaleLowerCase("tr").includes(term));
  }, [personelOptions, personelSearch]);

  const togglePersonel = (personel) => {
    setSelectedPersonel((prev) =>
      prev.some((p) => p.id === personel.id)
        ? prev.filter((p) => p.id !== personel.id)
        : [...prev, personel]
    );
  };

  const allFilteredSelected =
    filteredPersonel.length > 0 && filteredPersonel.every((p) => selectedPersonelIds.has(p.id));

  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredPersonel.map((p) => p.id));
      setSelectedPersonel((prev) => prev.filter((p) => !filteredIds.has(p.id)));
    } else {
      setSelectedPersonel((prev) => [
        ...prev,
        ...filteredPersonel.filter((p) => !selectedPersonelIds.has(p.id)),
      ]);
    }
  };

  const seciliRapor = RAPOR_TURLERI.find((r) => r.value === reportType) || RAPOR_TURLERI[0];

  const handleDownloadReport = async () => {
    if (!startDate || !endDate) {
      setError("Başlangıç ve bitiş tarihleri zorunludur");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError("Başlangıç tarihi bitiş tarihinden sonra olamaz");
      return;
    }

    if (seciliRapor.enFazlaGun) {
      const gunSayisi = Math.round((new Date(endDate) - new Date(startDate)) / 86400000) + 1;
      if (gunSayisi > seciliRapor.enFazlaGun) {
        setError(`${seciliRapor.label} için tarih aralığı en çok ${seciliRapor.enFazlaGun} gün olabilir`);
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);

      const params = {
        basla: startDate,
        bitir: endDate,
      };

      if (selectedNeighborhoods.length > 0) {
        params.mah = selectedNeighborhoods.map((n) => n.id).join(",");
      }

      if (selectedPersonel.length > 0) {
        params.personelids = selectedPersonel.map((p) => p.id).join(",");
      }

      const response = await seciliRapor.indir(params);
      const blob = response.data;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const today = new Date().toLocaleDateString("tr-TR").replace(/\./g, "-");
      link.href = url;
      link.download = `${seciliRapor.dosyaAdi}-${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download report:", error);
      setError((await raporHataMesaji(error)) || "Rapor indirme işlemi başarısız oldu");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="people-container">
      <h1 className="page-title">Raporlama</h1>

      {error && <div className="error-message">{error}</div>}

      <div className="search-filter-section">
        <div className="filter-container summary-filter-container raporlama-layout">
          <div className="raporlama-form">
            <div className="raporlama-field raporlama-tur">
              <label htmlFor="report-type">Rapor *</label>
              <select
                id="report-type"
                value={reportType}
                onChange={(e) => {
                  setReportType(e.target.value);
                  setError(null);
                }}
                className="p-inputtext p-component"
              >
                {RAPOR_TURLERI.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="raporlama-field raporlama-date">
              <label htmlFor="start-date">Başlangıç Tarihi *</label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setError(null);
                }}
                max={endDate || undefined}
                className="p-inputtext p-component date-input"
                required
              />
            </div>

            <div className="raporlama-field raporlama-date">
              <label htmlFor="end-date">Bitiş Tarihi *</label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setError(null);
                }}
                min={startDate || undefined}
                max={new Date().toISOString().split("T")[0]}
                className="p-inputtext p-component date-input"
                required
              />
            </div>

            <div className="raporlama-field raporlama-mahalle mahalle-group">
              <label htmlFor="neighborhood-select">Mahalle</label>
              <MultiSelect
                id="neighborhood-select"
                value={selectedNeighborhoods}
                options={neighborhoodOptions}
                onChange={handleNeighborhoodChange}
                optionLabel="name"
                placeholder={isNeighborhoodLoading ? "Mahalleler yükleniyor..." : "Mahalle Seçiniz"}
                className="mahalle-multiselect"
                disabled={isNeighborhoodLoading || neighborhoodOptions.length === 0}
                filter
                display="chip"
                maxSelectedLabels={3}
                selectedItemsLabel="{0} mahalle seçildi"
                appendTo="self"
              />
            </div>

            <button
              onClick={handleDownloadReport}
              className="download-excel-button"
              disabled={isLoading || !startDate || !endDate}
              style={{
                cursor: isLoading || !startDate || !endDate ? "not-allowed" : "pointer",
                opacity: isLoading || !startDate || !endDate ? 0.6 : 1,
              }}
            >
              <p>{isLoading ? "İndiriliyor..." : "Raporu İndir"}</p>
              {isLoading ? (
                <div className="spinner"></div>
              ) : (
                <ArrowDownIcon color="white" className="filter-icon" />
              )}
            </button>
          </div>

          {seciliRapor.aciklama && <p className="raporlama-aciklama">{seciliRapor.aciklama}</p>}

          <div className="raporlama-personel-panel">
            <div className="raporlama-personel-header">
              <span className="raporlama-personel-title">
                Personel
                {selectedPersonel.length > 0 && (
                  <span className="raporlama-personel-count">{selectedPersonel.length} seçildi</span>
                )}
              </span>
              <div className="raporlama-personel-actions">
                <button
                  type="button"
                  onClick={toggleAllFiltered}
                  disabled={filteredPersonel.length === 0}
                >
                  {allFilteredSelected ? "Seçimi Kaldır" : "Tümünü Seç"}
                </button>
                {selectedPersonel.length > 0 && (
                  <button type="button" onClick={() => setSelectedPersonel([])}>
                    Temizle
                  </button>
                )}
              </div>
            </div>
            <input
              type="text"
              className="p-inputtext p-component raporlama-personel-search"
              placeholder="Personel ara..."
              value={personelSearch}
              onChange={(e) => setPersonelSearch(e.target.value)}
            />
            <div className="raporlama-personel-list">
              {isPersonelLoading ? (
                <p className="modal-helper-text">Personel yükleniyor...</p>
              ) : filteredPersonel.length === 0 ? (
                <p className="modal-helper-text">Personel bulunamadı.</p>
              ) : (
                filteredPersonel.map((personel) => (
                  <label
                    key={personel.id}
                    className={`raporlama-personel-item${
                      selectedPersonelIds.has(personel.id) ? " selected" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPersonelIds.has(personel.id)}
                      onChange={() => togglePersonel(personel)}
                    />
                    <span>{personel.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Raporlama;
